import createDebug from 'debug';
import { Deserializer } from 'jsonapi-serializer';
import allReportStatus from '../constants/all-report-status';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { CardFormats, CardFormatsWrapper } from '../models/CardFormats';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { APIConnector } from './api/APIConnector';
import { createPagination } from './utilities/paginationBuilder';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';

const apiConnector = new APIConnector();
const debug = createDebug('card-format-service');

export const getCardFormats = async (listParameters?: ListQuery): Promise<CardFormatsWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `truncationFormat?fields[truncationFormat]=trnName,tfTagMask,tfSiteMask,totalTagTypes,tfDropZero,tfEditable&${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiCardFormats: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const cardFormats: CardFormats[] = apiCardFormats.map(apiCardFormat => {
                    return {
                        id: apiCardFormat['id'],
                        name: apiCardFormat['trn-name'],
                        tagMask: apiCardFormat['tf-tag-mask'],
                        siteMask: apiCardFormat['tf-site-mask'],
                        cardTypes: apiCardFormat['total-tag-types'],
                        dropZero: apiCardFormat['tf-drop-zero'],
                        status: apiCardFormat['tf-editable'] === 0 ? allReportStatus.Template.id : allReportStatus.Custom.id
                    }
                })

                return resolve({ rows: cardFormats, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCardFormatsById = async (cardFormatId): Promise<CardFormats> => {
    try {
        const url = `truncationFormat/${cardFormatId}?fields[truncationFormat]=trnName,tfTagMask,tfSiteMask,totalTagTypes,tfDropZero,tfEditable`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const cardFormat = {
                    id: data['id'],
                    name: data['trn-name'],
                    tagMask: data['tf-tag-mask'],
                    siteMask: data['tf-site-mask'],
                    cardTypes: data['total-tag-types'],
                    dropZero: data['tf-drop-zero'],
                    status: data['tf-editable'] === 0 ? allReportStatus.Template.id : allReportStatus.Custom.id
                }

                return resolve(cardFormat);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteTruncationFormat = async (id: string): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `truncationFormat/${id}`;
        const reportType = {
            data: {
                type: "truncationFormat",
                id: id,
            },
        };
        const response = await apiConnector.deleteData(url, reportType);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({ id: id, success: false, status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const storeCardFormat = async (cardFormat : CardFormats, createCardFormat = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: "truncationFormat",
                id: cardFormat.id,
                attributes: {
                    trnName: cardFormat.name,
                    tfDropZero: cardFormat.dropZero,
                    tfEditable: 1,
                    tfFormat: null,
                    tfPacsBits: 0,
                    tfSiteMask: cardFormat.siteMask,
                    tfTagMask: cardFormat.tagMask,
                    totalTagTypes: cardFormat.cardTypes,
                }
            },
        };

        const url = `truncationFormat${createCardFormat ? "" : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector
            .sendData(url, createCardFormat, newRecord)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createCardFormat) {
                    const content = await response.json();

                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail), success: false })
                    }
                    new Deserializer().deserialize(content, (error, cardFormat) => {
                        if (error) {
                            return reject({ status: response.status, id: cardFormat.id, success: false });
                        }
                        return resolve({ id: cardFormat.id, success: true });
                    })

                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: cardFormat.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: cardFormat.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: cardFormat.id, success: false, message: genericError });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCardFormatsWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `truncationFormat?fields[truncationFormat]=trnName${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const cardFormats: Name[] = data.map((cardFormat): Name => {
                    return {
                        value: cardFormat['id'],
                        label: cardFormat['trn-name']
                    };
                });

                return resolve({ rows: cardFormats, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
